"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { TrendingUp, Users, DollarSign, Zap, CheckCircle2, Share2, ArrowRight, Sparkles, ShieldCheck, ChevronRight } from "lucide-react";
import { getPlanLimits } from "@/lib/plans";

export default function ReferralPublic() {
  const searchParams = useSearchParams();
  const referralCode = searchParams.get("ref");
  
  useEffect(() => {
    import("@/lib/referral-persistence").then(
      ({ captureReferralCode, initializeReferralTracking }) => {
        initializeReferralTracking();
        if (referralCode) {
          captureReferralCode(referralCode);
        }
      }
    );
  }, [referralCode]);

  const commission = 20;
  const professionalPlan = getPlanLimits("professional");
  const commissionRate = commission / 100;
  const monthlyFrom10 = professionalPlan.price * commissionRate * 10;
  const monthlyFrom50 = professionalPlan.price * commissionRate * 50;
  const yearlyFrom50 = monthlyFrom50 * 12;

  const formatPounds = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded)
      ? rounded.toString()
      : rounded.toFixed(2);
  };

  const signupUrl = referralCode ? `/referral-signup?ref=${encodeURIComponent(referralCode)}` : "/referral-signup";
  const loginUrl = referralCode ? `/referral-login?ref=${encodeURIComponent(referralCode)}` : "/referral-login";

  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-900 overflow-x-hidden">
      
      {/* ── 1. HERO SECTION (MATCHING HOMEPAGE & FEATURES BRANDING) ────────── */}
      <section className="relative overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 text-white pt-12 pb-16 md:pt-16 md:pb-24">
        {/* Ambient background glow */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Col: Hero Copy (7 Cols) */}
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
                GARAGEBOOST PARTNER & AFFILIATE PROGRAM
              </span>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight text-white tracking-tight">
                Earn{" "}
                <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                  {commission}% Recurring
                </span>{" "}
                Commission on Every UK Garage
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed font-normal">
                Recommend GarageBoost to MOT stations, mobile mechanics, and automotive service centers. Earn recurring monthly income for the lifetime of every active subscription.
              </p>

              <div className="space-y-3 pt-2">
                {[
                  `Earn ${commission}% on every monthly & annual paid subscription`,
                  "Fast automated payouts 7 days after first invoice",
                  "Zero earning caps — unlimited recurring upside",
                  "Dedicated affiliate dashboard with real-time conversion tracking"
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-teal-500/20 border border-teal-500/30 rounded-full flex items-center justify-center shrink-0">
                      <CheckCircle2 className="text-teal-400" size={14} />
                    </div>
                    <span className="text-sm sm:text-base text-slate-200">{text}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-4">
                <Link
                  href={signupUrl}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-9 py-4 rounded-xl font-bold text-base sm:text-lg shadow-xl shadow-blue-500/40 hover:shadow-blue-500/60 hover:scale-105 transition-all"
                >
                  <span>Join as Partner</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <Link
                  href={loginUrl}
                  className="w-full sm:w-auto inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 px-8 py-4 rounded-xl font-bold text-base sm:text-lg transition-all"
                >
                  Partner Dashboard Login
                </Link>
              </div>
            </div>

            {/* Right Col: Earnings Calculator Card (5 Cols) */}
            <div className="lg:col-span-5">
              <div className="relative bg-linear-to-b from-slate-900 to-slate-950 rounded-3xl p-7 sm:p-8 border-2 border-blue-500/40 shadow-2xl shadow-blue-500/20 space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <span className="text-xs font-bold text-teal-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles size={14} /> Passive Income Simulator
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                    20% Recurring
                  </span>
                </div>

                {/* 10 Garages Box */}
                <div className="bg-slate-800/60 rounded-2xl p-5 border border-slate-700/60 space-y-1">
                  <div className="flex items-center justify-between text-xs text-slate-400 font-medium">
                    <span className="flex items-center gap-1.5">
                      <Users size={14} className="text-blue-400" /> Refer 10 Pro Garages
                    </span>
                    <span className="text-slate-400">£59/mo plan</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-black text-white pt-1">
                    £{formatPounds(monthlyFrom10)}<span className="text-sm font-semibold text-slate-400"> / month</span>
                  </p>
                  <p className="text-xs text-slate-400 pt-0.5">Recurring passive income deposited every month</p>
                </div>

                {/* 50 Garages Box */}
                <div className="bg-linear-to-br from-blue-950/60 to-slate-900/90 rounded-2xl p-5 border border-blue-500/40 space-y-1">
                  <div className="flex items-center justify-between text-xs text-teal-300 font-medium">
                    <span className="flex items-center gap-1.5">
                      <Zap size={14} className="text-teal-400" /> Scale to 50 Garages
                    </span>
                    <span className="text-teal-400 font-bold">Top Partner Tier</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-black text-teal-300 pt-1">
                    £{formatPounds(monthlyFrom50)}<span className="text-sm font-semibold text-teal-200/80"> / month</span>
                  </p>
                  <p className="text-xs text-teal-200/70 font-semibold pt-0.5">
                    Equal to <span className="text-white font-bold">£{formatPounds(yearlyFrom50)}</span> / year in predictable recurring revenue
                  </p>
                </div>

                <div className="pt-2 text-center text-xs text-slate-400">
                  Instant sign up • Custom referral link provided immediately
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── 2. TRUST STATS BAR ────────────────────────────────────────────── */}
      <section className="py-6 bg-slate-900 border-b border-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-800 text-center">
            <div className="py-2 px-4">
              <p className="text-xl sm:text-2xl font-extrabold text-white">20% Lifetime</p>
              <p className="text-xs text-slate-400 mt-0.5">Recurring monthly commission</p>
            </div>
            <div className="py-2 px-4">
              <p className="text-xl sm:text-2xl font-extrabold text-blue-400">7-Day Payouts</p>
              <p className="text-xs text-slate-400 mt-0.5">Fast automated bank transfers</p>
            </div>
            <div className="py-2 px-4">
              <p className="text-xl sm:text-2xl font-extrabold text-teal-400">Zero Earnings Cap</p>
              <p className="text-xs text-slate-400 mt-0.5">Scale with unlimited garages</p>
            </div>
            <div className="py-2 px-4">
              <p className="text-xl sm:text-2xl font-extrabold text-cyan-400">Live Analytics</p>
              <p className="text-xs text-slate-400 mt-0.5">UTM link generator & tracking</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. HOW IT WORKS ────────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest">
            3 SIMPLE STEPS
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900">
            How the GarageBoost Referral Program Works
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            Start earning recurring revenue from day one with simple sharing tools.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "01",
              title: "Share Your Unique Link",
              desc: "Get your personalized tracking link. Share it with automotive forums, MOT stations, mechanic networks, or via email & social media.",
            },
            {
              step: "02",
              title: "Garages Start Free Trial",
              desc: "When a workshop registers via your link, our tracking system automatically tags them to your partner account for life.",
            },
            {
              step: "03",
              title: "Collect Monthly Payouts",
              desc: `Receive ${commission}% of their subscription every single month they stay active. Payouts are transferred automatically.`,
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-3xl p-8 border-2 border-slate-200/90 hover:border-blue-300 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 font-black text-lg flex items-center justify-center mb-6 border border-blue-100">
                  {item.step}
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 4. PARTNER DASHBOARD FEATURES ──────────────────────────────────── */}
      <section className="py-16 md:py-20 bg-slate-100 border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-600 block">
                PARTNER PORTAL
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900">
                A Dedicated Dashboard to Track Every Click & Conversion
              </h2>
              <p className="text-slate-600 text-base leading-relaxed">
                Our modern partner dashboard gives you full transparency into clicks, trial activations, active paid subscriptions, and pending commission payouts.
              </p>

              <div className="space-y-4 pt-2">
                {[
                  "Real-time click and conversion telemetry with channel tracking",
                  "1-click link generator with custom UTM campaign tags",
                  "Direct payout ledger with downloadable monthly statements",
                  "Marketing assets, banners, and UK garage email templates",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="text-blue-600 shrink-0 mt-0.5" size={18} />
                    <span className="text-sm sm:text-base font-medium text-slate-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-200/90 shadow-xl space-y-6">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="text-blue-600" size={22} /> What You Receive as a Partner:
                </h3>
                <ul className="space-y-3.5 text-sm text-slate-700">
                  <li className="flex items-center gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                    <strong>Unique Referral Code & Links</strong> (e.g., garage.bizzboost.uk?ref=YOURNAME)
                  </li>
                  <li className="flex items-center gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-teal-600"></span>
                    <strong>60-Day Cookie Window</strong> for long attribution cycles
                  </li>
                  <li className="flex items-center gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                    <strong>Recurring Revenue Reports</strong> with monthly payout tracking
                  </li>
                  <li className="flex items-center gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-teal-600"></span>
                    <strong>Direct Partner Support</strong> from our UK team
                  </li>
                </ul>

                <div className="pt-4 border-t border-slate-100">
                  <Link
                    href={signupUrl}
                    className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <span>Create Partner Account</span>
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── 5. SIGNATURE BOTTOM CTA FLOATING CARD ──────────────────────────── */}
      <section className="py-14 sm:py-20 bg-slate-50 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl p-8 sm:p-14 md:p-16 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 border border-slate-800 shadow-2xl shadow-blue-950/40 text-white text-center space-y-6">
            
            {/* Ambient inner glows */}
            <div className="absolute top-0 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

            <div className="relative z-10 space-y-6 max-w-3xl mx-auto">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
                START EARNING TODAY
              </span>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
                Ready to Build{" "}
                <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                  Passive Recurring Income?
                </span>
              </h2>

              <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed">
                Join our partner network today. It takes less than 2 minutes to create an account and generate your first tracking link.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href={signupUrl}
                  className="group w-full sm:w-auto bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-9 py-4 rounded-xl font-bold text-base sm:text-lg shadow-xl shadow-blue-500/40 hover:shadow-blue-500/60 hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  <span>Sign Up as Referrer</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <Link
                  href={loginUrl}
                  className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 px-8 py-4 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center gap-2"
                >
                  Sign In to Dashboard
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
